<template>
  <div class="items-wrapper">
    <header class="page-header fade-in">
      <div class="header-copy">
        <h1>Articles</h1>
        <p>
          Visualisez et gérez rapidement votre catalogue d'articles sans changer
          d'écran.
        </p>
      </div>
      <div class="header-actions">
        <button
          class="btn btn-outline"
          @click="refreshItems"
          :disabled="loading"
        >
          <i class="bi bi-arrow-clockwise"></i>
          <span>Actualiser</span>
        </button>
        <button class="btn btn-primary" @click="goToCreate">
          <i class="bi bi-plus-circle"></i>
          <span>Nouvel article</span>
        </button>
      </div>
    </header>

    <section>
      <div class="filters">
        <div class="search">
          <i class="bi bi-search"></i>
          <input
            v-model="searchTerm"
            type="search"
            placeholder="Rechercher (nom, code, référence...)"
          />
        </div>
        <div class="stats" v-if="filteredItems.length">
          <span>{{ filteredItems.length }} article(s)</span>
        </div>
      </div>

      <div v-if="loading" class="state">
        <div class="loader"></div>
        <p>Chargement des articles…</p>
      </div>

      <div v-else-if="filteredItems.length === 0" class="state">
        <i class="bi bi-box-seam state-icon"></i>
        <h3>Aucun article trouvé</h3>
        <p>Essayez une autre recherche ou ajoutez un nouvel article.</p>
        <button class="btn btn-primary" @click="goToCreate">
          <i class="bi bi-plus-circle"></i>
          <span>Créer un article</span>
        </button>
      </div>

      <div v-else class="item-cards">
        <article
          v-for="item in filteredItems"
          :key="item.reference || item.id || item.fullName"
          class="item-card fade-in"
          @click="viewItem(item.reference)"
        >
          <div class="item-card-body">
            <div class="item-image-frame">
              <img
                v-if="hasItemImage(item)"
                :src="itemImageSource(item)"
                :alt="item.fullName || item.name || item.reference || 'Article'"
                loading="lazy"
                @error="markImageError(item)"
              />
              <div v-else class="item-image-placeholder">
                <i class="bi bi-image"></i>
              </div>
            </div>

            <div class="card-content">
              <div class="item-header">
                <h3 class="item-name">
                  {{ item.fullName || item.name || item.reference }}
                </h3>
                <span v-if="item.code" class="item-code">{{ item.code }}</span>
              </div>
              <p v-if="item.description" class="item-description">
                {{ item.description }}
              </p>
              <div class="item-info">
                <div v-if="item.barCode" class="info-item">
                  <i class="bi bi-upc-scan"></i>
                  <span>{{ item.barCode }}</span>
                </div>
                <div v-if="item.priceTTC != null" class="info-item price-item">
                 MAD
                  <span>{{ formatPrice(item.priceTTC) }}</span>
                </div>
              </div>
            </div>
          </div>
        </article>
      </div>
    </section>

    <button class="fab" @click="goToCreate">
      <i class="bi bi-plus-lg"></i>
    </button>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "ItemsList",
  data() {
    return {
      loading: false,
      searchTerm: "",
      imageLoadErrors: {},
    };
  },
  computed: {
    ...mapGetters("item", { items: "getAll" }),
    filteredItems() {
      if (!this.searchTerm) return this.items || [];
      const term = this.searchTerm.toLowerCase();
      return (this.items || []).filter((item) =>
        [
          item.fullName,
          item.name,
          item.code,
          item.barCode,
          item.reference,
          item.description,
        ]
          .filter(Boolean)
          .map((value) => value.toString().toLowerCase())
          .some((value) => value.includes(term))
      );
    },
  },
  methods: {
    itemKey(item) {
      return item?.reference || item?.id || item?.fullName || item?.name || "";
    },
    itemImageSource(item) {
      return this.$imageUrl(item.image);
    },
    hasItemImage(item) {
      const key = this.itemKey(item);
      return Boolean(item?.image && !this.imageLoadErrors[key]);
    },
    markImageError(item) {
      const key = this.itemKey(item);
      if (key) {
        this.$set(this.imageLoadErrors, key, true);
      }
    },
    formatPrice(price) {
      if (price == null) return "—";
      return new Intl.NumberFormat("fr-FR", {
        style: "currency",
        currency: "MAD",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(price);
    },
    async fetchItems() {
      this.loading = true;
      try {
        await this.$store.dispatch("item/getAll");
      } catch (error) {
        console.error("Error loading items", error);
        this.$store.dispatch("notification/store", {
          status: "error",
          message: "Impossible de charger les articles.",
        });
      } finally {
        this.loading = false;
      }
    },
    refreshItems() {
      this.fetchItems();
    },
    goToCreate() {
      this.$router.push({ name: "items-create" });
    },
    viewItem(reference) {
      this.$router.push({ name: "items-show", params: { reference } });
    },
  },
  mounted() {
    if (!this.items || this.items.length === 0) {
      this.fetchItems();
    }
  },
};
</script>

<style scoped>
.items-wrapper {
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  padding: 2rem 2.5rem 5rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 2rem;
}

.page-header h1 {
  margin: 0 0 0.5rem;
  font-size: 2.5rem;
  font-weight: 800;
  background: linear-gradient(135deg, #1e293b 0%, #475569 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  letter-spacing: -0.02em;
}

.page-header p {
  margin: 0;
  max-width: 520px;
  color: #64748b;
  font-size: 1rem;
  line-height: 1.6;
}

.header-actions {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.filters {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #f1f5f9;
}

.search {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.875rem 1.25rem;
  border-radius: 12px;
  background: #f8fafc;
  border: 2px solid #e2e8f0;
  min-width: 300px;
  flex: 1;
  transition: all 0.2s ease;
}

.search:focus-within {
  border-color: #3b82f6;
  background: white;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.search i {
  color: #94a3b8;
  font-size: 1.1rem;
  flex-shrink: 0;
}

.search input {
  border: none;
  background: transparent;
  outline: none;
  font-size: 0.95rem;
  width: 100%;
  color: #1e293b;
}

.search input::placeholder {
  color: #94a3b8;
}

.stats {
  color: #64748b;
  font-weight: 600;
  font-size: 0.9rem;
  padding: 0.5rem 1rem;
  background: #f1f5f9;
  border-radius: 8px;
}

.state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 4rem 1rem;
  color: #64748b;
  text-align: center;
}

.state-icon {
  font-size: 4rem;
  color: #cbd5e1;
  opacity: 0.6;
}

.state h3 {
  margin: 0;
  font-size: 1.25rem;
  color: #475569;
  font-weight: 600;
}

.state p {
  margin: 0;
  color: #94a3b8;
  font-size: 0.95rem;
}

.loader {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: 4px solid #e2e8f0;
  border-top-color: #3b82f6;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.item-cards {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1rem;
}

.item-card {
  background: white;
  border-radius: 14px;
  padding: 1.25rem;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04), 0 4px 8px rgba(0, 0, 0, 0.04);
  border: 1px solid #e2e8f0;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  position: relative;
  overflow: hidden;
}

.item-card::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, #3b82f6, #8b5cf6);
  transform: scaleX(0);
  transform-origin: left;
  transition: transform 0.3s ease;
}

.item-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08), 0 8px 24px rgba(0, 0, 0, 0.06);
  border-color: #cbd5e1;
}

.item-card:hover::before {
  transform: scaleX(1);
}

.item-card-body {
  display: flex;
  gap: 1rem;
  align-items: stretch;
  min-height: 136px;
}

.item-image-frame {
  width: 112px;
  min-width: 112px;
  aspect-ratio: 1;
  border-radius: 12px;
  overflow: hidden;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.item-image-frame img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.item-image-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #cbd5e1;
  font-size: 2rem;
}

.card-content {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  min-width: 0;
  flex: 1;
}

.item-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.5rem;
}

.item-name {
  margin: 0;
  font-size: 1.15rem;
  color: #0f172a;
  font-weight: 700;
  line-height: 1.3;
  letter-spacing: -0.01em;
  flex: 1;
}

.item-code {
  background: #f1f5f9;
  color: #64748b;
  padding: 0.25rem 0.5rem;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  white-space: nowrap;
}

.item-description {
  margin: 0;
  color: #64748b;
  font-size: 0.9rem;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.item-info {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: 0.5rem;
  padding-top: 0.75rem;
  border-top: 1px solid #f1f5f9;
}

.info-item {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  color: #475569;
  font-size: 0.875rem;
  line-height: 1.5;
}

.info-item i {
  color: #3b82f6;
  font-size: 0.9rem;
  flex-shrink: 0;
}

.info-item span {
  word-break: break-word;
}

.price-item {
  font-weight: 600;
  color: #059669;
}

.price-item i {
  color: #059669;
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  border-radius: 10px;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  border: none;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  white-space: nowrap;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn i {
  font-size: 1rem;
}

.btn:hover:not(:disabled) {
  transform: translateY(-1px);
}

.btn-primary {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}

.btn-primary:hover:not(:disabled) {
  box-shadow: 0 6px 16px rgba(59, 130, 246, 0.4);
}

.btn-outline {
  border: 2px solid #e2e8f0;
  background: white;
  color: #475569;
}

.btn-outline:hover:not(:disabled) {
  border-color: #3b82f6;
  color: #3b82f6;
  background: #f8fafc;
}

.fab {
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);
  color: white;
  border: none;
  font-size: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 16px rgba(59, 130, 246, 0.3), 0 4px 8px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 100;
}

.fab:hover {
  transform: scale(1.1) translateY(-2px);
  box-shadow: 0 12px 24px rgba(59, 130, 246, 0.4),
    0 6px 12px rgba(0, 0, 0, 0.15);
}

.fab:active {
  transform: scale(0.95);
}

.fade-in {
  animation: fadeIn 0.4s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 1024px) {
  .items-wrapper {
    padding: 1.5rem 1.5rem 5rem;
  }
  .page-header {
    flex-direction: column;
  }
  .header-actions {
    width: 100%;
    justify-content: flex-start;
  }
  .item-cards {
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  }
}

@media (max-width: 768px) {
  .items-wrapper {
    padding: 1.25rem 1rem 5rem;
  }
  .page-header h1 {
    font-size: 2rem;
  }
  .search {
    min-width: unset;
  }
  .item-cards {
    grid-template-columns: 1fr;
    gap: 0.875rem;
  }
  .fab {
    bottom: 1.5rem;
    right: 1.5rem;
    width: 52px;
    height: 52px;
    font-size: 1.4rem;
  }
}

@media (max-width: 480px) {
  .page-header h1 {
    font-size: 1.75rem;
  }
  .item-card-body {
    gap: 0.875rem;
    min-height: 96px;
  }
  .item-image-frame {
    width: 88px;
    min-width: 88px;
    border-radius: 10px;
  }
  .item-card {
    padding: 1rem;
  }
  .item-name {
    font-size: 1.05rem;
  }
  .btn {
    padding: 0.625rem 1rem;
    font-size: 0.875rem;
  }
}
</style>
